import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {CommonModule} from '@angular/common';
import {MatLineModule} from '@angular/material/core';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {Subscription} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {UserService} from '../api/services/user.service';
import {MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {ToursService} from '../api/services/tour.service';
import {CreateNewTourDialogComponent} from './create-new-tour-dialog.component';
import {MapService} from '../api/services/map.service';
import {MapTour} from '../api/interfaces/map-tour';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-tour-list',
  styleUrls: ['./tour-list.bottomsheet.component.scss'],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-object-story me-2"></span>{{ 'TOURS.LIST.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="dismiss()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <mat-nav-list class="list-wrapper">
      <a mat-list-item *ngFor="let tour of tours"
         (click)="selectTour(tour); $event.preventDefault(); $event.stopPropagation()"
         class="d-flex justify-content-evenly p-0">
        <span class="row w-100 align-items-center m-0">
          <span class="col-2 align-items-center d-flex">
              <mat-icon class="material-symbols-rounded"
                        *ngIf="!tour.excludeFromReadOnly"
                        [matTooltip]="'TOURS.LIST.VISIBLE_TOOLTIP' | translate">visibility</mat-icon>
              <mat-icon class="material-symbols-rounded"
                        [matTooltip]="'TOURS.LIST.HIDDEN_TOOLTIP' | translate"
                        *ngIf="tour.excludeFromReadOnly">visibility_off</mat-icon>
          </span>
          <span class="col-6 pe-5"
                [matTooltip]="tour.description || tour.name"
                matTooltipPosition="right">
              <span matLine>{{tour.name}}</span>
          </span>
          <span class="col-4  d-flex justify-content-end">
            <button mat-icon-button
                    [matTooltip]="'TOURS.LIST.EDIT_TOOLTIP' | translate"
                    (click)="edit(tour);$event.preventDefault();  $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">edit</mat-icon>
            </button>

            <button mat-icon-button
                    [matTooltip]="'TOURS.LIST.DELETE_TOOLTIP' | translate"
                    (click)="askForConfirmation(tour);$event.preventDefault();  $event.stopPropagation()">
                    <mat-icon class="material-symbols-rounded">delete</mat-icon>
            </button>
          </span>
        </span>
      </a>
    </mat-nav-list>
    <mat-divider></mat-divider>
    <div class="py-2" style="padding-left: 10px;">
          <button class="dialog-action-btn dialog-action-btn-primary"
                  [matTooltip]="'TOURS.LIST.CREATE_TOOLTIP' | translate"
                  (click)="createNew(); $event.preventDefault();">
              {{ 'TOURS.LIST.CREATE_BUTTON' | translate }}
          </button>
    </div>
  `,

  standalone: true,
  imports: [
    MatListModule,
    CommonModule,
    MatLineModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    MatTooltipModule,
    TranslatePipe,
  ]
})

export class TourListBottomsheetComponent implements OnInit, OnDestroy {
  public tours: any[] = [];
  private subSink: Subscription = new Subscription();

  constructor(private matDialog: MatDialog,
              private userService: UserService,
              private mapService: MapService,
              private snackBar: MatSnackBar,
              private toursService: ToursService,
              private translate: TranslateService,
              private bottomSheetRef: MatBottomSheetRef<TourListBottomsheetComponent>) {
  }

  ngOnInit() {
    this.subSink.add(
      (isInIframe() ? this.toursService.getAllPublic(this.mapService.getCurrentSelectedMapFromStore().id) : this.toursService.getAll(this.mapService.getCurrentSelectedMapFromStore().id))
        .subscribe((tours) => {
        this.tours = tours;
      })
    );
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  selectTour(tour: MapTour) {
    if (tour.steps && tour.steps.length > 0 ) {
      this.bottomSheetRef.dismiss(tour);
    } else {
      this.snackBar.open(this.translate.instant('TOURS.LIST.NO_STEPS'), this.translate.instant('TOURS.LIST.DISMISS'), {duration: 60000});
    }
  }

  createNew() {
    const ref = this.matDialog.open(CreateNewTourDialogComponent);
    ref.afterClosed().subscribe((tour: any) => {
      if (tour) {
        (isInIframe() ?
          this.toursService.getAllPublic(this.mapService.getCurrentSelectedMapFromStore().id) :
          this.toursService.getAll(this.mapService.getCurrentSelectedMapFromStore().id)
        )
          .subscribe((tours) => {
          this.tours = tours;
        })
      }
    });
  }

  edit(tour: MapTour) {
    const ref = this.matDialog.open(CreateNewTourDialogComponent, {data: tour});
    ref.afterClosed().subscribe((v: any) => {
      if (v) {
        (isInIframe() ?
        this.toursService.getAllPublic(this.mapService.getCurrentSelectedMapFromStore().id) :
        this.toursService.getAll(this.mapService.getCurrentSelectedMapFromStore().id)
        )
          .subscribe((tours) => {
          this.tours = tours;
        })
      }
    });
  }

  delete(tour: MapTour) {
    this.toursService.delete(tour).subscribe((res) => {
      this.tours = this.tours.filter((t) => t.id !== tour.id);
    });
  }

  dismiss() {
    this.bottomSheetRef.dismiss();
  }

  askForConfirmation(tour: MapTour) {
    const ref = this.matDialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('TOURS.LIST.DELETE_TITLE'),
        message: this.translate.instant('TOURS.LIST.DELETE_MESSAGE', {name: tour.name}),
        confirmText: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM')
      }
    });
    ref.afterClosed().subscribe((res) => {
      if (res) {
        this.delete(tour);
      }
    });
  }
}
